import { useLayoutEffect, useState } from 'react'

import useMapDataContext, { MapData, MapView } from '@contexts/mapDataContext'
import useCurrentLocationContext from '@contexts/currentLocationContext'
import { isAvatarSrc, thumbnailLargeNoFallback, thumbnailMediumBlue, thumbnailMediumNoFallback } from '@utils/thumbnails'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuthContext } from '@contexts/authContext'
import useDrawerContext from '@contexts/drawerContext'
import useModalContext from '@contexts/modalContext'
import { colors, fontSize, size } from '@styles/theme'
import useDialog from '@hooks/useDialog'
// import axios from '@utils/webRequests'
import distance from '@turf/distance'
import styled from '@emotion/styled'
import clamp from 'clamp-js'

import { ProfileBottomDrawerContents } from '@components/layout/bottom_drawers/ProfileBottomDrawerContents'
import { SocialNearbyProfileButton } from '@elements/buttons/SocialNearbyProfileButton'
import NearbySettingsDialog from '../../forms/profile_forms/MainProfileForm'
import { ChatActionBtn } from '@elements/FloatingActionBtn'
import { SentOrReply } from '@elements/icons/SentOrReply'
import FloatingButton from '@elements/FloatingButton'
import AnimationImage from '@elements/AnimationImage'
import NearbyImageViewer from './NearbyImageViewer'
import AtAGlanceDetails from './AtAGlanceDetails'
import { SubTopbar } from './SubTopbar'
import {
  PiDotsThreeVerticalBold,
  PiCaretLeftBold,
  PiCrosshairBold,
  PiProhibitBold,
  PiGearSixBold,
} from 'react-icons/pi'

const TopBar = styled.nav`
  display: flex;
  align-items: center;
  justify-content: space-between;

  position: absolute;
  top: 0;
  left: 0;

  padding: 0 ${size(1)};
  width: 100dvw; // WARNING: you tried to make this 100%, but the width changes on screen navigation
  height: ${size(7)};
  z-index: 2;
  border-bottom: 0.5px solid transparent;
  transition-duration: 300ms;
`

const HeaderRightContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`

const ButtonFloat = styled(FloatingButton)`
  padding: ${size(1)};
  height: auto;
  min-width: 0;
  transition-duration: 300ms;
`

const ImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  flex-shrink: 0;

  position: absolute;
  top: 0rem;
  right: 0;

  z-index: 1;

  margin-right: ${size(1)};
  margin-top: ${size(11)};
`

const UserImageWrapper = styled.div`
  &::before {
    content: '';
    height: 100%;
    width: 100%;
    border-radius: 100px;
    position: absolute;
    top: 0;
    left: 0;
    box-shadow: 0px 10px 10px rgba(255,255,255,0.15) inset;
  }
  position: relative;
  border-radius: 100px;
  margin-bottom: ${size(1.5)};
  height: ${size(11)};
  width: ${size(11)};
  border: 0.5px solid rgba(255,255,255, 0.5);
  animation: growAndFadeIn975 ease-in 250ms;
  animation-fill-mode: backwards;
`

const UserImage = styled(AnimationImage)`
  height: 100%;
  width: 100%;
  border-radius: 100px;
  box-shadow: 0px 4px 6px rgba(0,0,0,0.5);
  object-fit: cover;
`

const AtAGlanceHeader = styled.div`
  font-weight: bold;
  text-align: left;
  padding: 0 ${size(0.5)} 0 ${size(1)};
  font-size: ${fontSize('sm')};
  margin-right: auto;
  z-index: 1;
  text-shadow: 0 1px 4px #333333CC;
`

const BackButton = styled.div`
  display: flex;
  align-items: center;
`

const NewReplyText = styled.span`
  position: absolute;
  top: ${size(-1.25)};
  right: 0;
  color: ${colors.onlineIndicator()};
  font-size: 8px;
  font-weight: 900;
`

const NearbyProfileTopbar = ({ user, setUser }) => {
  const [isNearbySettingsOpen, openNearbySettings, closeNearbySettings] = useDialog('nearby_profile_settings', 'nearby-profile-settings-open', { params: { form_display: 'nearby-social' } })
  const [isImageViewerOpen, openImageViewer, closeImageViewer] = useDialog(`nearby_image_viewer`, 'nearby-image-viewer-open')
  const { currentLocation } = useCurrentLocationContext()
  const { map, profilesInitialized } = useMapDataContext()
  const { openBottomDrawer } = useDrawerContext()
  const { state: authState } = useAuthContext()
  const navigate = useNavigate()
  const [userDistance, setUserDistance] = useState(null)
  const [isUserOnMap, setIsUserOnMap] = useState(true)
  const isCurrentUser = (user?.nearbyUuid === authState?.user?.nearbyUuid)
  const [isAboveBrief, setIsAboveBrief] = useState(true)
  const isNavigatedFromSocial = !!useLocation()?.state?.isNavigatedFromSocial
  const { messagesModal } = useModalContext()
  const socialSrc = thumbnailMediumBlue(user?.socialMedia?.thumbnail, user?.defaultAvatar)
  const isAvatar = isAvatarSrc('medium', user?.socialMedia?.thumbnail, user?.defaultAvatar)
  const mapUserData = MapData.getProfile(user?.nearbyUuid)
  const lastMessageIsReply = mapUserData?.lastMessage?.nearbyUuid && mapUserData?.lastMessage?.nearbyUuid === mapUserData?.nearbyUuid 
  const lastMessageIsNewRepy = lastMessageIsReply && mapUserData?.lastMessage?.isNew

  // Gets distance between users and formats result for rendering
  useLayoutEffect(() => {
    const mapLocation = MapData.getProfile(user?.nearbyUuid)?.lonlat
    if (!user || !mapLocation || !currentLocation) return;
    // Handle the scenario where the current profile is your own
    if (isCurrentUser) {
      setUserDistance('0 ft')
      return;
    }
    // Find the distance
    let distanceBetweenUsers = distance(currentLocation, mapLocation, { units: 'feet' })

    // If the distance is less than 0.75 miles, render in feet, else render in miles
    if (distanceBetweenUsers < 3960) {
      if (distanceBetweenUsers < 100) distanceBetweenUsers = 100;
      setUserDistance(`${distanceBetweenUsers.toFixed(0)} ft`)
    } else {
      distanceBetweenUsers = distanceBetweenUsers / 5280 // in miles
      distanceBetweenUsers = Number.parseFloat((Math.round(distanceBetweenUsers * 8) / 8).toFixed(2))
      setUserDistance(`${distanceBetweenUsers} mi`)
    }
  }, [user, currentLocation])

  useLayoutEffect(() => {
    if (user && profilesInitialized) {
      const newMapUser = MapData.getProfile(user?.nearbyUuid)
      if (newMapUser) {
        setIsUserOnMap(true)
      } else {
        setIsUserOnMap(false)
      }
    }
  }, [user, profilesInitialized])

  useLayoutEffect(() => {
    const briefTopEl = document.querySelector('#nearby-profile__brief-top')
    if (!briefTopEl) return;
    const observer = new window.IntersectionObserver(([entry]) => {
      if (entry.boundingClientRect.top < 0) {
        // IS BELOW ANCHOR
        setIsAboveBrief(false)
      } else {
        // IS ABOVE ANCHOR
        setIsAboveBrief(true)
      }
    }, { threshold: 0 })

    // NOTE: If the scroll position doesn't get set before we do this calculation,
    //       Then the threshold will erroneously say you're above, even when below
    //       causing the elements to render as if you were at the top of the page
    setTimeout(() => {
      observer.observe(briefTopEl)
    }, 10)

    // Cleanup state and observer
    return () => {
      setIsAboveBrief(true)
      observer.unobserve(briefTopEl)
    }
  }, [])

  const navigateToUserOnMap = () => {
    const mapUser = MapData.getProfile(user?.nearbyUuid)
    if (map && isUserOnMap && mapUser?.lonlat) {
      MapView.focusOnUser(user)
      navigate('/map', { search: '' })
      map?.easeTo({ center: mapUser.lonlat, zoom: 14, duration: 2000 })
    }
  }

  const openMessagesModal = () => {
    messagesModal.open(null, { state: { user, conversationType: 'nearby' } })
  }

  const renderUserActions = () => (
    <ButtonFloat
      style={{ backgroundColor: !isAboveBrief && '#333333' }}
      onClick={() => openBottomDrawer(
        <ProfileBottomDrawerContents
          isOnNearbyProfile
          otherUser={user}
        />
      )}>
      <PiDotsThreeVerticalBold size={size(2.5)} />
    </ButtonFloat>
  )

  const renderUserSettings = () => (
    <ButtonFloat
      style={{ backgroundColor: !isAboveBrief && '#333333' }}
      onClick={() => openNearbySettings()}>
      <PiGearSixBold size={size(2.5)} />
    </ButtonFloat>
  )

  const renderUserDistance = () => {
    if (!profilesInitialized) {
      return 'map not set'
    } else if (!isUserOnMap) {
      return 'not on map'
    } else if (userDistance) {
      return userDistance
    } else {
      return 'not on map'
    }
  }

  const updateOwnUser = (updatedUser) => {
    if (user && authState?.user && isCurrentUser) {
      setUser(updatedUser)
    }
  }

  const socialProfileNavigation = () => {
    // We want to backnavigate if we navigated from the social profile so we don't get stuck in looped backnavigation later
    if (isNavigatedFromSocial) {
      navigate(-1)
    } else {
      navigate(`/profiles/${user?.uuid}`, { state: { isNavigatedFromNearbyProfile: true } })
    }
  }

  const renderSettingsOrActions = () => {
    return isCurrentUser
      ? renderUserSettings()
      : renderUserActions()
  }

  return (
    <>
      <NearbySettingsDialog
        isOpen={isNearbySettingsOpen}
        setClosed={closeNearbySettings}
        afterSubmit={updateOwnUser}
        setUser={setUser}
      />
      <NearbyImageViewer
        openNearbySettings={openNearbySettings}
        closeNearbySettings={closeNearbySettings}
        closeImageViewer={closeImageViewer}
        open={isImageViewerOpen}
        user={isCurrentUser ? authState?.user : user}
        isCurrentUser={isCurrentUser}
      />
      <TopBar
        style={{
          backgroundColor: !isAboveBrief && 'rgba(20,20,20, 0.9)',
          backdropFilter: !isAboveBrief && 'blur(10px)',
          borderBottomColor: !isAboveBrief && 'rgba(255,255,255, 0.15)',
          // height: !isAboveBrief && size(7.5),
        }}>
        <BackButton>
          <ButtonFloat
            onClick={() => navigate(-1)}
            style={{ backgroundColor: !isAboveBrief && '#333333' }}>
            <PiCaretLeftBold size={size(2.5)} />
          </ButtonFloat>
        </BackButton>
        {!isAboveBrief &&
          <AtAGlanceHeader
            style={{ fontSize: (user?.isAnonymous) ? fontSize('sm') : fontSize('xs') }}
            ref={node => node && clamp(node, {clamp: 2})}>
            <AtAGlanceDetails
              displayRegisteredIcon={false}
              capitalizeEach
              hasUserSuffix
              user={user}
            />
          </AtAGlanceHeader>
        }
        <HeaderRightContainer>
          {isAboveBrief && // display above brief, but remove for registered+ users to make space for tagline
            <ButtonFloat
              disabled={!isUserOnMap || !profilesInitialized}
              style={{
                marginRight: size(1),
                color: (!isUserOnMap || !profilesInitialized || !userDistance) ? 'grey' : 'white',
                backgroundColor: !isAboveBrief && '#333333',
              }} 
              onClick={() => navigateToUserOnMap()}>
              {(isUserOnMap && profilesInitialized)
                ? <PiCrosshairBold size={size(2.5)} />
                : <PiProhibitBold size={size(2.5)} />
              }
              &nbsp;{renderUserDistance()}
            </ButtonFloat>
          }
          {!user?.isAnonymous && user?.uuid && (
            <SocialNearbyProfileButton
              isAboveAnchor={isAboveBrief}
              onNavigate={socialProfileNavigation}
              thumbnailSrc={socialSrc}
              isAvatar={isAvatar}
              buttonText='Social'
              postCount={user?.postCount}
            />
          )}
          {renderSettingsOrActions()}
        </HeaderRightContainer>
      </TopBar>
      {!isAboveBrief && user && <SubTopbar user={user} />}
      <ImageContainer>
        {user?.nearbyMedia?.thumbnails?.[1] &&
          <UserImageWrapper onClick={() => openImageViewer({ thumbnailIndex: 1 })}>
            <UserImage src={thumbnailMediumNoFallback(user?.nearbyMedia?.thumbnails?.[1])} />
          </UserImageWrapper>
        }
        {user?.nearbyMedia?.thumbnails?.[2] &&
          <UserImageWrapper onClick={() => openImageViewer({ thumbnailIndex: 2 })}>
            <UserImage src={thumbnailMediumNoFallback(user?.nearbyMedia?.thumbnails?.[2])} />
          </UserImageWrapper>
        }
        {user?.nearbyMedia?.thumbnails?.[3] &&
          <UserImageWrapper onClick={() => openImageViewer({ thumbnailIndex: 3 })}>
            <UserImage src={thumbnailMediumNoFallback(user?.nearbyMedia?.thumbnails?.[3])} />
          </UserImageWrapper>
        }
      </ImageContainer>
      {user && authState?.user && user?.nearbyUuid !== authState?.user?.nearbyUuid &&
        <ChatActionBtn openMessagesModal={openMessagesModal}>
          <SentOrReply
            mapUserData={mapUserData}
            size={size(2.5)}
            style={{ position: 'absolute', top: size(-0.2), [lastMessageIsReply ? 'right' : 'left']: size(0) }}
          />
          {lastMessageIsNewRepy && <NewReplyText>NEW</NewReplyText>}
        </ChatActionBtn>
      }
    </>
  )
}

export default NearbyProfileTopbar
